import React from 'react';

import { Bar, BarChart, Rectangle, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Stack, Typography, useTheme } from '@mui/material';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';
import { BRAND_COLORS } from '~/components/brand/brandTokens';

import { COLOURS, Model, ReportView, VIEW_CHARTS, finite, format } from './presentationModel';

/** Chart titles from each workbook dashboard (KPI Net Margin - Experience, KPI Activities - Agent Hub, ...). */
const LABELS: Record<string, string> = {
  net_margin_adjusted: 'Net Margin (Excl. Exp)',
  net_signings: 'Net Signings',
  calls: 'Calls / VC',
  meetings: 'Meetings',
  proposals: 'Proposals',
  activity_point: 'Total Activity',
  no_of_activated_ah_agents: 'Activated Agents',
};
const VIEW_LABELS: Partial<Record<ReportView, Record<string, string>>> = {
  Experience: { net_margin_adjusted: 'Net Margin (Experience)' },
  'Agent Hub': { no_of_activated_ah_agents: 'No of Activated Agents' },
};

/** Workbook horizontal bullet charts for the view: forecast behind actual, with a target marker. */
export default function PerformanceChart({
  model,
  view,
  quarter,
  asOf,
}: {
  model: Model;
  view: ReportView;
  quarter: string;
  asOf: string;
}) {
  const theme = useTheme();
  const order = VIEW_CHARTS[view];
  return (
    <Box sx={{ minWidth: 0, minHeight: 450, display: 'flex', flexDirection: 'column', height: 'auto', px: 2, py: 2 }}>
      <Stack direction="row" spacing={1.25} sx={{ flexShrink: 0, pb: 0.25 }}>
        {[
          ['Actual', COLOURS.actual],
          ['Forecast', COLOURS.forecast],
          ['Target', COLOURS.target],
        ].map(([label, colour]) => (
          <Typography key={label} variant="caption" component="span">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 13,
                height: 5,
                mr: 0.5,
                bgcolor: colour,
                outline: `1px solid ${theme.palette.mode === 'dark' ? BRAND_COLORS.compassWhite : BRAND_COLORS.oceanTeal}`,
              }}
            />
            {label}
          </Typography>
        ))}
      </Stack>
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gridAutoRows: 'minmax(130px, auto)',
          gap: 2,
          minHeight: 0,
        }}
      >
        {order.map((id, index) => {
          const metric = model.metrics.find((item) => item.id === id);
          if (!metric) {
            return null;
          }
          const values = [metric.actual, metric.forecast, metric.target].filter(finite);
          const min = Math.min(0, ...values);
          const max = Math.max(1, ...values);
          const label = VIEW_LABELS[view]?.[id] ?? LABELS[id] ?? id;
          const unit = metric.unit === 'money' ? 'A$' : 'count';
          return (
            <Box
              key={id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 130,
                // The lead chart spans the row; two-chart dashboards (Experience, Agent Hub) stack full width.
                gridColumn: index === 0 || order.length <= 2 ? '1 / -1' : 'auto',
              }}
            >
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Typography variant="caption" fontWeight={600} color="primary.main">
                  {label} · {unit}
                </Typography>
                <Typography variant="caption">Forecast to target {format(metric.forecastRatio, 'percent')}</Typography>
              </Stack>
              <Box
                sx={{ flex: 1, minHeight: 60 }}
                role="img"
                aria-label={`${label}: actual ${format(metric.actual, metric.unit)}, forecast ${format(metric.forecast, metric.unit)}, target ${format(metric.target, metric.unit)} ${unit}`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={[{ name: label, Actual: metric.actual, Forecast: metric.forecast }]}
                    barGap={-18}
                    margin={{ top: 8, right: 42, bottom: 2, left: 0 }}
                  >
                    <XAxis
                      type="number"
                      domain={[min, max * 1.06]}
                      height={22}
                      tickLine={false}
                      tickCount={4}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      tickFormatter={(value) =>
                        new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(value)
                      }
                    />
                    <YAxis type="category" dataKey="name" hide />
                    <ReportChartTooltip
                      cursor={false}
                      content={() => (
                        <Box
                          sx={{
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            p: 1,
                            border: 1,
                            borderColor: 'divider',
                            fontSize: 12,
                          }}
                        >
                          <strong>{label}</strong>
                          <br />
                          Actual: {format(metric.actual, metric.unit)} {unit}
                          <br />
                          Forecast: {format(metric.forecast, metric.unit)} {unit}
                          <br />
                          Target: {format(metric.target, metric.unit)} {unit}
                          <br />
                          Actual to target: {format(metric.actualRatio, 'percent')}
                          <br />
                          Forecast to target: {format(metric.forecastRatio, 'percent')}
                        </Box>
                      )}
                    />
                    <Bar
                      dataKey="Forecast"
                      fill={COLOURS.forecast}
                      stroke={BRAND_COLORS.oceanTeal}
                      strokeWidth={1}
                      barSize={18}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="Actual"
                      fill={COLOURS.actual}
                      stroke={theme.palette.mode === 'dark' ? BRAND_COLORS.compassWhite : COLOURS.actual}
                      strokeWidth={1}
                      barSize={18}
                      isAnimationActive={false}
                      shape={(props) => (
                        <Rectangle {...props} y={Number(props.y) + (Number(props.height) - 9) / 2} height={9} />
                      )}
                    />
                    {finite(metric.target) && (
                      <ReferenceLine x={metric.target} stroke={BRAND_COLORS.compassWhite} strokeWidth={4} />
                    )}
                    {finite(metric.target) && (
                      <ReferenceLine
                        x={metric.target}
                        stroke={COLOURS.target}
                        strokeWidth={2}
                        label={{
                          value: 'Target',
                          position: 'insideTopRight',
                          fill: theme.palette.text.primary,
                          fontSize: 12,
                        }}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12, flexShrink: 0 }}>
        analytics.pcm_tracker_unpivot
        {view === 'Agent Hub' ? ' · team totals from agent_hub_team (net margin counted once)' : ''} · quarter {quarter}{' '}
        · source modified {asOf}
      </Typography>
    </Box>
  );
}
